import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useSWRImmutable from 'swr/immutable';

import { type BonusOrigin, BonusType, normalizeBonuses } from '@/api/cms';
import { fetcherCMS } from '@/modules/api';
import {
  CMSSortTypeEnum,
  createCMSQuery,
  DEFAULT_CMS_PAGE_SIZE,
  getCMSDurationQuery,
  getCMSImageFieldsUrl,
  getCMSLocale,
  getCMSSchedulerQuery,
  getCMSShowGuestQuery,
} from '@/modules/cms';
import { useMatchingSections } from '@/modules/restrictions/hooks/useAccessSections';
import { useGetCommonStore } from '@/store';

export const useCMSCasinoBonuses = () => {
  const {
    i18n: { language },
  } = useTranslation();
  const useCommonStore = useGetCommonStore();

  const isAuthenticated = useCommonStore.use.isAuthenticated();

  const query = useMemo(
    () =>
      createCMSQuery({
        sort: [`order:${CMSSortTypeEnum.ASC}`],
        filters: {
          $and: [
            getCMSSchedulerQuery(),
            getCMSDurationQuery(),
            { promo_page_toggle: { $eq: true } },
            { type_offer: { $eq: BonusType.CASINO } },
            ...getCMSShowGuestQuery(isAuthenticated),
          ],
        },
        populate: {
          scheduler: '*',
          ...getCMSImageFieldsUrl('image'),
        },
        ...getCMSLocale(language),
        pagination: {
          pageSize: DEFAULT_CMS_PAGE_SIZE,
        },
      }),
    [language, isAuthenticated]
  );

  const {
    data = [],
    error,
    isLoading,
  } = useSWRImmutable<BonusOrigin[]>(`/bonuses-news?${query}`, fetcherCMS);

  const bonuses = useMatchingSections(normalizeBonuses(data));

  return {
    data,
    isLoading,
    isError: error,
    bonuses,
  };
};
